import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import { getReferenceString } from '@medplum/core';
import {
  Bundle,
  DiagnosticReport,
  Communication,
  Patient,
  Practitioner,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { HL7 } from '@consts/systems.consts';
import {
  DiagnosticReportReviewAction,
  DiagnosticReportReviewStatus,
  OrderSubState,
} from '../../lims-types';
import {
  addReviewStatusExtension,
  checkAllReviewersApproved,
  completeReviewerTask,
  createFollowUpTask,
  findReviewerTask,
} from '../../utils/fhir-helpers';
import { updateOrderSubState } from '../../utils/order-substates-helper';

// -----------------------------------------------------------------------------
interface UseCommentSubmissionProps {
  reportId?: string;
  diagnosticReport: DiagnosticReport | null;
  currentUser?: Practitioner;
  patient?: Patient;
  serviceRequest?: ServiceRequest;
  onCommentSubmitted?: () => void;
  onReviewStatusChange?: (status: DiagnosticReportReviewStatus) => void;
  onDiagnosticReportUpdate?: (report: DiagnosticReport) => void;
  /** Called when the report is finalized to generate the PDF blob for storage */
  generatePdfBlob?: () => Promise<Blob | null>;
}

// -----------------------------------------------------------------------------
export const useCommentSubmission = ({
  reportId,
  diagnosticReport,
  currentUser,
  patient,
  serviceRequest,
  onCommentSubmitted,
  onReviewStatusChange,
  onDiagnosticReportUpdate,
  generatePdfBlob,
}: UseCommentSubmissionProps) => {
  const { t } = useTranslation('lims');
  const medplum = useMedplum();
  const [submittingComment, setSubmittingComment] = useState(false);
  const [reviewStatus, setReviewStatus] =
    useState<DiagnosticReportReviewAction>('comment');
  const [reviewComment, setReviewComment] = useState('');

  // ---------------------------------------------------------------------------
  const submitComment = async () => {
    if (
      !reviewComment.trim() ||
      !reportId ||
      !diagnosticReport ||
      !currentUser?.id
    ) {
      return;
    }

    setSubmittingComment(true);
    try {
      const communication: Communication = {
        resourceType: 'Communication',
        status: 'completed',
        category: [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.BASE,
                code:
                  reviewStatus === 'comment'
                    ? HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.CODES
                        .INSTRUCTION
                    : HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.CODES
                        .NOTIFICATION,
                display:
                  reviewStatus === 'comment' ? 'Instruction' : 'Notification',
              },
            ],
          },
        ],
        subject: diagnosticReport.subject as any,
        basedOn: [{ reference: `DiagnosticReport/${reportId}` }],
        sender: { reference: `Practitioner/${currentUser.id}` },
        sent: new Date().toISOString(),
        payload: [
          {
            contentString: reviewComment.trim(),
          },
        ],
        note: [
          {
            text:
              reviewStatus === 'comment'
                ? 'Review comment'
                : `Review status: ${reviewStatus}`,
            time: new Date().toISOString(),
          },
        ],
      };

      await medplum.createResource(communication);

      // If this is a review (not just a comment), handle the task completion and approval workflow
      if (reviewStatus !== 'comment') {
        // Find the current reviewer's task
        const reviewerTask = await findReviewerTask(
          medplum,
          reportId,
          currentUser.id
        );

        if (reviewerTask) {
          // Complete the reviewer's task
          await completeReviewerTask(
            medplum,
            reviewerTask,
            reviewStatus,
            reviewComment.trim()
          );
        }

        // Check if all reviewers have approved
        const allReviewStatus = await checkAllReviewersApproved(
          medplum,
          reportId
        );

        // Update current user's review status
        onReviewStatusChange?.(reviewStatus);

        // Update the diagnostic report with review status extension
        const reportWithReviewStatus = addReviewStatusExtension(
          diagnosticReport,
          allReviewStatus
        );

        // If all reviewers have approved and this is an approval, finalize the report
        if (
          allReviewStatus.allApproved &&
          reviewStatus === 'approved' &&
          diagnosticReport.status === 'preliminary'
        ) {
          // Generate PDF and store as presentedForm
          let presentedForm = diagnosticReport.presentedForm;
          try {
            const pdfBlob = await generatePdfBlob?.();
            if (pdfBlob) {
              const binary = await medplum.createBinary({
                data: pdfBlob,
                filename: `report-${reportId}.pdf`,
                contentType: 'application/pdf',
              });
              presentedForm = [
                {
                  contentType: 'application/pdf',
                  url: getReferenceString(binary),
                  title: `report-${reportId}.pdf`,
                },
              ];
            }
          } catch (pdfError) {
            console.error(
              'Error generating PDF for finalized report:',
              pdfError
            );
          }

          const finalReport = {
            ...reportWithReviewStatus,
            status: 'final' as const,
            issued: new Date().toISOString(),
            presentedForm,
          };

          // Atomically finalize the report and advance the order substate
          if (serviceRequest?.id) {
            const latestSR = await medplum.readResource(
              'ServiceRequest',
              serviceRequest.id
            );
            const updatedSR = updateOrderSubState(
              latestSR,
              OrderSubState.FollowUpTasks
            );
            const bundle: Bundle = {
              resourceType: 'Bundle',
              type: 'transaction',
              entry: [
                {
                  resource: finalReport,
                  request: {
                    method: 'PUT',
                    url: `DiagnosticReport/${finalReport.id}`,
                  },
                },
                {
                  resource: updatedSR,
                  request: {
                    method: 'PUT',
                    url: `ServiceRequest/${serviceRequest.id}`,
                  },
                },
              ],
            };
            await medplum.executeBatch(bundle);
          } else {
            await medplum.updateResource(finalReport);
          }
          onDiagnosticReportUpdate?.(finalReport);

          // Create follow-up tasks for the finalized report
          if (patient) {
            try {
              await Promise.all([
                medplum.createResource(
                  createFollowUpTask(
                    finalReport,
                    patient,
                    'inform-referral-doctor'
                  )
                ),
                medplum.createResource(
                  createFollowUpTask(finalReport, patient, 'contact-patient')
                ),
              ]);
            } catch (followUpError) {
              console.error('Error creating follow-up tasks:', followUpError);
            }
          }

          notifications.show({
            title: t('review.notifications.reportFinalized'),
            message: t('review.notifications.reportFinalizedMessage'),
            color: 'green',
          });
        } else {
          // Just update the review status extension
          await medplum.updateResource(reportWithReviewStatus);
          onDiagnosticReportUpdate?.(reportWithReviewStatus);

          if (reviewStatus === 'approved') {
            notifications.show({
              title: t('review.notifications.reviewApproved'),
              message: t('review.notifications.reviewApprovedMessage', {
                pending: allReviewStatus.pendingTasks,
              }),
              color: 'blue',
            });
          } else {
            notifications.show({
              title: t('review.notifications.changesRequested'),
              message: t('review.notifications.changesRequestedMessage'),
              color: 'orange',
            });
          }
        }
      } else {
        // Just a comment
        notifications.show({
          title: t('review.notifications.commentAdded'),
          message: t('review.notifications.commentAddedMessage'),
          color: 'green',
        });
      }

      setReviewComment('');
      setReviewStatus('comment');
      onCommentSubmitted?.();
    } catch (error: any) {
      notifications.show({
        title:
          reviewStatus === 'comment'
            ? t('review.notifications.errorAddingComment')
            : t('review.notifications.errorSubmittingReview'),
        message:
          error.message ||
          (reviewStatus === 'comment'
            ? t('review.notifications.errorAddingCommentMessage')
            : t('review.notifications.errorSubmittingReviewMessage')),
        color: 'red',
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  // ---------------------------------------------------------------------------
  return {
    submittingComment,
    reviewStatus,
    setReviewStatus,
    reviewComment,
    setReviewComment,
    submitComment,
  };
};
